/*
 * Pure helpers for the video panel editor: legacy URL→embed generation ported from
 * packages/main/resources/templates/desktop/html/page/components/admin_panel-item-video.vue.html,
 * plus safe parsing of stored embed code for the editor preview. The preview is always rebuilt
 * from parsed attributes — pasted HTML is never injected into the DOM.
 */

// Keep in sync with ALLOWED_IFRAME_HOSTNAMES in packages/core-api/src/services/Sanitizer.ts —
// the allowlist the server-side sanitiser will enforce when the save mutation lands.
const ALLOWED_IFRAME_HOSTNAMES = [
  'calendar.google.com',
  'docs.google.com',
  'embed.buto.tv',
  'embed.ted.com',
  'fast.wistia.com',
  'fast.wistia.net',
  'media.t-mobile.com',
  'onedrive.live.com',
  'oursites.dhsstaff.dhs.gov.au',
  'play.buto.tv',
  'play.vidyard.com',
  'player.vimeo.com',
  'players.brightcove.net',
  'storify.com',
  'videomarketingplatform.co',
  'video.twentythree.net',
  'viewhomes.formstack.com',
  'w.soundcloud.com',
  'w2.countingdownto.com',
  'web.microsoftstream.com',
  'www.boombox.com',
  'www.cincopa.com',
  'www.facebook.com',
  'www.google.com',
  'www.powr.io',
  'www.surveymonkey.co.uk',
  'www.surveymonkey.com',
  'www.youtube-nocookie.com',
  'www.youtube.com'
];

const YOUTUBE_ID_PATTERN = /^.*(youtu.be\/|v\/|e\/|u\/\w+\/|embed\/|v=)([^#&?]*).*/;
const VIMEO_ID_PATTERN = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/;
const TED_EMBED_PREFIX = 'https://embed.ted.com/';

const IFRAME_EMBED_PATTERN = /^<iframe[\s\S]*<\/iframe>$/i;
const VIDEO_TAG_PATTERN = /^<video\b[\s\S]*<\/video>$/i;

export interface ParsedEmbed {
  kind: 'iframe' | 'video';
  src: string;
  width?: string;
  height?: string;
}

export const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/*
 * Legacy hostname extraction: strips protocol/auth/www, then one more subdomain label
 * (m.youtube.com → youtube.com) — youtu.be survives because the second pattern needs two dots.
 */
const getHostname = (source: string): string | undefined => {
  const match = source.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n?=]+)/im);
  if (!match) {
    return undefined;
  }
  const subdomainMatch = match[1].match(/^[^.]+\.(.+\..+)$/);
  return subdomainMatch ? subdomainMatch[1] : match[1];
};

/**
 * Builds an iframe embed for a recognised video host (youtube/vimeo/ted), or null when the
 * host or video id is unrecognised. Dimensions and normalisation mirror the legacy editor.
 */
export const generateEmbedCodeFromUrl = (videoUrl: string): string | null => {
  switch (getHostname(videoUrl)) {
    case 'youtube.com':
    case 'youtu.be':
    case 'youtube-nocookie.com': {
      const videoId = videoUrl.match(YOUTUBE_ID_PATTERN)?.[2];
      return videoId
        ? `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}"></iframe>`
        : null;
    }
    case 'vimeo.com': {
      const videoId = videoUrl.match(VIMEO_ID_PATTERN)?.[5];
      return videoId
        ? `<iframe width="640" height="360" src="https://player.vimeo.com/video/${videoId}"></iframe>`
        : null;
    }
    case 'ted.com': {
      const src = videoUrl
        .replace('https://www.ted.com/', TED_EMBED_PREFIX)
        .replace('https://ted.com/', TED_EMBED_PREFIX);
      return `<iframe width="854" height="480" src="${src}"></iframe>`;
    }
    default:
      return null;
  }
};

export const buildVideoTagEmbedCode = (videoUrl: string, fallbackText: string): string =>
  `<video controls width="620" src="${videoUrl.replace(/"/g, '&quot;')}">${fallbackText}</video>`;

/**
 * Serialises a parsed embed back to canonical markup. The editor preview reuses the public
 * community-page <Video> renderer (dangerouslySetInnerHTML), so it must only ever receive
 * markup rebuilt from the validated attributes — never the raw pasted code.
 */
export const buildSafeEmbedCode = (parsed: ParsedEmbed, fallbackText: string): string => {
  if (parsed.kind === 'video') {
    return buildVideoTagEmbedCode(parsed.src, fallbackText);
  }
  const width = parsed.width && /^\d+$/.test(parsed.width) ? ` width="${parsed.width}"` : '';
  const height = parsed.height && /^\d+$/.test(parsed.height) ? ` height="${parsed.height}"` : '';
  return `<iframe${width}${height} src="${parsed.src.replace(/"/g, '&quot;')}"></iframe>`;
};

const isAllowedIframeSrc = (src: string): boolean => {
  try {
    const url = new URL(src);
    return url.protocol === 'https:' && ALLOWED_IFRAME_HOSTNAMES.includes(url.hostname);
  } catch {
    return false;
  }
};

/**
 * Parses stored embed code into the attributes the preview is rebuilt from. Iframes must be a
 * single <iframe> wrapper (legacy gate) with an https src on the sanitiser allowlist; <video>
 * tags only need an http(s) src (direct video files can live on any host and execute no script).
 */
export const parseEmbedCode = (embedCode: string): ParsedEmbed | null => {
  const trimmed = embedCode.trim();

  if (IFRAME_EMBED_PATTERN.test(trimmed)) {
    const iframe = new DOMParser().parseFromString(trimmed, 'text/html').querySelector('iframe');
    const src = iframe?.getAttribute('src') ?? '';
    return isAllowedIframeSrc(src)
      ? {
          kind: 'iframe',
          src,
          width: iframe?.getAttribute('width') ?? undefined,
          height: iframe?.getAttribute('height') ?? undefined
        }
      : null;
  }

  if (VIDEO_TAG_PATTERN.test(trimmed)) {
    const video = new DOMParser().parseFromString(trimmed, 'text/html').querySelector('video');
    const src = video?.getAttribute('src') ?? '';
    return isHttpUrl(src) ? { kind: 'video', src } : null;
  }

  return null;
};
